import { asbError } from '@project/common/util/log';
import type { LogProvider } from '@project/common/util/log';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import SettingsTextField from '@project/common/components/SettingsTextField';
import SwitchLabelWithHoverEffect from '@project/common/components/SwitchLabelWithHoverEffect';
import LabelWithHoverEffect from '@project/common/components/LabelWithHoverEffect';
import type { AsbplayerSettings } from '@project/common/settings';
import {
    autoPausePreferenceForCheckboxChange,
    AutoPauseResumeMode,
    SubtitleVisibility,
    isTrackAutoCopyable,
    isTrackSeekable,
    PauseOnHoverMode,
    SubtitleListTimestampDisplay,
    updateAutoCopyableTracksValue,
    updateSeekableTracksValue,
    VideoSubtitleSplitBehavior,
} from '@project/common/settings';
import { exportSettings, mergeImportedSettings, validateSettings } from '@project/common/settings/import-export';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoPausePreference, SubtitleHtml } from '@project/common';
import { WebSocketClient } from '@project/common/web-socket-client';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsSection, { SettingsSubSection } from '@project/common/components/SettingsSection';
import ResponsiveSettingsStack from '@project/common/components/ResponsiveSettingsStack';
import { normalizePlaybackRate } from '@project/common/playback/controllers/playback-mode-controller';
import { normalizeAutoPauseDurationBounds } from '@project/common/playback/plan/playback-plan';
import InfoIcon from '@mui/icons-material/Info';
import NumericSettingInput from '@project/common/components/NumericSettingInput';
import KeyboardShortcutLink from '@project/common/components/KeyboardShortcutLink';
import LogViewerDialog from '@project/common/components/LogViewerDialog';
import BuildIcon from '@mui/icons-material/Build';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ButtonGroup from '@mui/material/ButtonGroup';
import NoWrapButton from '@project/common/components/NoWrapButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

function regexIsValid(regex: string) {
    try {
        new RegExp(regex.trim());
        return true;
    } catch {
        return false;
    }
}

interface Props {
    settings: AsbplayerSettings;
    onSettingChanged: <K extends keyof AsbplayerSettings>(key: K, value: AsbplayerSettings[K]) => Promise<void>;
    onSettingsChanged: (settings: Partial<AsbplayerSettings>) => void;
    logProvider: LogProvider;
    supportedLanguages: string[];
    insideApp?: boolean;
    extensionInstalled?: boolean;
    extensionSupportsPauseOnHover?: boolean;
    extensionSupportsSeekableTrackSetting?: boolean;
    extensionSupportsAutoCopyableTrackSetting?: boolean;
    supportsSubtitleListCustomization: boolean;
    supportsPlaybackEngine: boolean;
    supportsAutoPauseResume: boolean;
    onViewPlaybackModeKeyboardShortcuts: () => void;
    onViewPlaybackRateKeyboardShortcuts: () => void;
    onViewSubtitleKeyboardShortcuts: () => void;
}

const MiscSettingTab: React.FC<Props> = ({
    settings,
    onSettingChanged,
    onSettingsChanged,
    logProvider,
    supportedLanguages,
    insideApp,
    extensionInstalled,
    extensionSupportsPauseOnHover,
    extensionSupportsSeekableTrackSetting,
    extensionSupportsAutoCopyableTrackSetting,
    supportsSubtitleListCustomization,
    supportsPlaybackEngine,
    supportsAutoPauseResume,
    onViewPlaybackModeKeyboardShortcuts,
    onViewPlaybackRateKeyboardShortcuts,
    onViewSubtitleKeyboardShortcuts,
}) => {
    const { t } = useTranslation();
    const {
        themeType,
        videoSubtitleSplitBehavior,
        showSubtitleListMiningButton,
        subtitleListTimestampDisplay,
        language,
        rememberSubtitleOffset,
        autoCopyCurrentSubtitle,
        seekableTracks,
        autoCopyableTracks,
        miningHistoryStorageLimit,
        subtitleRegexFilter,
        tabName,
        subtitleRegexFilterTextReplacement,
        subtitleHtml,
        convertNetflixRuby,
        pauseOnHoverMode,
        webSocketClientEnabled,
        webSocketServerUrl,
        subtitleAboveThumbnail,
        thumbnailPreview,
        autoPausePreference,
        subtitleTriggerStartOffset,
        subtitleTriggerEndOffset,
        subtitleTriggerGapEndOffset,
        subtitleTriggerGapStartOffset,
        playbackRate,
        playbackRateNotificationEnabled,
        rememberPlaybackRate,
        fastForwardModePlaybackRate,
        fastForwardPlaybackMinimumSkipIntervalMs,
        repeatCountPreference,
        autoPauseResumeMode,
        autoPauseResumeDelayMs,
        autoPauseFixedDurationMs,
        autoPauseMinimumDurationMs,
        autoPauseMaximumDurationMs,
        autoPauseTimePerCharacterMs,
        subtitleVisibility,
        rememberPlaybackModes,
        streamingCondensedPlaybackMinimumSkipIntervalMs,
    } = settings;
    const autoPauseAtStart = autoPausePreference !== AutoPausePreference.atEnd;
    const autoPauseAtEnd = autoPausePreference !== AutoPausePreference.atStart;
    const handleAutoPausePreferenceChanged = useCallback(
        (edge: AutoPausePreference.atStart | AutoPausePreference.atEnd, checked: boolean) => {
            void onSettingChanged(
                'autoPausePreference',
                autoPausePreferenceForCheckboxChange(autoPausePreference, edge, checked)
            );
        },
        [autoPausePreference, onSettingChanged]
    );
    const handleAutoPauseMinimumDurationChanged = useCallback(
        (minimumDurationMs: number) => {
            const bounds = normalizeAutoPauseDurationBounds(minimumDurationMs, autoPauseMaximumDurationMs);
            onSettingsChanged({
                autoPauseMinimumDurationMs: bounds.minimumDurationMs,
                autoPauseMaximumDurationMs: bounds.maximumDurationMs,
            });
        },
        [autoPauseMaximumDurationMs, onSettingsChanged]
    );
    const handleAutoPauseMaximumDurationChanged = useCallback(
        (maximumDurationMs: number) => {
            const bounds = normalizeAutoPauseDurationBounds(autoPauseMinimumDurationMs, maximumDurationMs);
            onSettingsChanged({
                autoPauseMinimumDurationMs: bounds.minimumDurationMs,
                autoPauseMaximumDurationMs: bounds.maximumDurationMs,
            });
        },
        [autoPauseMinimumDurationMs, onSettingsChanged]
    );
    const validRegex = useMemo(() => regexIsValid(subtitleRegexFilter), [subtitleRegexFilter]);
    const [webSocketConnectionSucceeded, setWebSocketConnectionSucceeded] = useState<boolean>();
    const [logViewerOpen, setLogViewerOpen] = useState(false);
    const pingWebSocketServer = useCallback(() => {
        const client = new WebSocketClient();
        client
            .bind(webSocketServerUrl)
            .then(() => client.ping())
            .then(() => setWebSocketConnectionSucceeded(true))
            .catch((e) => {
                asbError('settings/web-socket', e);
                setWebSocketConnectionSucceeded(false);
            })
            .finally(() => client.unbind());
    }, [webSocketServerUrl]);
    useEffect(() => {
        if (webSocketClientEnabled && webSocketServerUrl) {
            pingWebSocketServer();
        }
    }, [pingWebSocketServer, webSocketClientEnabled, webSocketServerUrl]);

    let webSocketServerUrlHelperText: string | null | undefined = undefined;

    if (webSocketClientEnabled) {
        if (webSocketConnectionSucceeded) {
            webSocketServerUrlHelperText = t('info.connectionSucceeded');
        } else if (webSocketConnectionSucceeded === false) {
            webSocketServerUrlHelperText = t('info.connectionFailed');
        }
    }

    const settingsFileInputRef = useRef<HTMLInputElement>(null);
    const handleSettingsFileInputChange = useCallback(async () => {
        try {
            const file = settingsFileInputRef.current?.files?.[0];

            if (file === undefined) {
                return;
            }

            const importedSettings = JSON.parse(await file.text());
            const validatedSettings = validateSettings(mergeImportedSettings(importedSettings, settings));
            onSettingsChanged(validatedSettings);
        } catch (e) {
            asbError('settings/import', e);
        }
    }, [onSettingsChanged, settings]);

    const handleImportSettings = useCallback(() => {
        settingsFileInputRef.current?.click();
    }, []);
    const handleExportSettings = useCallback(() => {
        exportSettings(settings);
    }, [settings]);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            <Stack spacing={1}>
                <SettingsSection>{t('settings.tools')}</SettingsSection>
                <ButtonGroup size="small" variant="contained" orientation={isSmallScreen ? 'vertical' : 'horizontal'}>
                    <NoWrapButton fullWidth startIcon={<UploadIcon />} onClick={handleImportSettings}>
                        {t('action.importSettings')}
                    </NoWrapButton>
                    <NoWrapButton fullWidth startIcon={<DownloadIcon />} onClick={handleExportSettings}>
                        {t('action.exportSettings')}
                    </NoWrapButton>
                    <NoWrapButton fullWidth startIcon={<BuildIcon />} onClick={() => setLogViewerOpen(true)}>
                        {t('settings.logs')}
                    </NoWrapButton>
                </ButtonGroup>

                <SettingsSection>{t('settings.ui')}</SettingsSection>
                <FormControl>
                    <FormLabel>{t('settings.theme')}</FormLabel>
                    <RadioGroup row>
                        <LabelWithHoverEffect
                            control={
                                <Radio
                                    checked={themeType === 'light'}
                                    value="light"
                                    onChange={(event) =>
                                        event.target.checked && void onSettingChanged('themeType', 'light')
                                    }
                                />
                            }
                            label={t('settings.themeLight')}
                        />
                        <LabelWithHoverEffect
                            control={
                                <Radio
                                    checked={themeType === 'dark'}
                                    value="dark"
                                    onChange={(event) =>
                                        event.target.checked && void onSettingChanged('themeType', 'dark')
                                    }
                                />
                            }
                            label={t('settings.themeDark')}
                        />
                    </RadioGroup>
                </FormControl>
                <SettingsTextField
                    select
                    label={t('settings.language')}
                    value={language}
                    color="primary"
                    onChange={(event) => onSettingChanged('language', event.target.value)}
                >
                    {supportedLanguages.map((s) => (
                        <MenuItem key={s} value={s}>
                            {s}
                        </MenuItem>
                    ))}
                </SettingsTextField>
                <SwitchLabelWithHoverEffect
                    control={
                        <Switch
                            checked={videoSubtitleSplitBehavior === VideoSubtitleSplitBehavior.autoMaximizeVideo}
                            onChange={(event) =>
                                onSettingChanged(
                                    'videoSubtitleSplitBehavior',
                                    event.target.checked
                                        ? VideoSubtitleSplitBehavior.autoMaximizeVideo
                                        : VideoSubtitleSplitBehavior.rememberSplitPosition
                                )
                            }
                        />
                    }
                    label={t('videoSubtitleSplitBehavior.autoMaximizeVideo')}
                    labelPlacement="start"
                />
                {supportsSubtitleListCustomization && (
                    <>
                        <SwitchLabelWithHoverEffect
                            control={
                                <Switch
                                    checked={showSubtitleListMiningButton}
                                    onChange={(event) =>
                                        onSettingChanged('showSubtitleListMiningButton', event.target.checked)
                                    }
                                />
                            }
                            label={t('settings.showSubtitleListMiningButton')}
                            labelPlacement="start"
                        />
                        <FormControl>
                            <FormLabel>{t('settings.subtitleListTimestamps')}</FormLabel>
                            <RadioGroup
                                row
                                value={subtitleListTimestampDisplay}
                                onChange={(event) =>
                                    onSettingChanged(
                                        'subtitleListTimestampDisplay',
                                        event.target.value as SubtitleListTimestampDisplay
                                    )
                                }
                            >
                                {Object.values(SubtitleListTimestampDisplay).map((value) => (
                                    <LabelWithHoverEffect
                                        key={value}
                                        control={<Radio value={value} />}
                                        label={t(`subtitleListTimestampDisplay.${value}`)}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </>
                )}
                <SettingsSection>{t('settings.subtitles')}</SettingsSection>
                <SwitchLabelWithHoverEffect
                    control={
                        <Switch
                            checked={rememberSubtitleOffset}
                            onChange={(event) => onSettingChanged('rememberSubtitleOffset', event.target.checked)}
                        />
                    }
                    label={t('settings.rememberSubtitleOffset')}
                    labelPlacement="start"
                />
                <SwitchLabelWithHoverEffect
                    control={
                        <Switch
                            checked={autoCopyCurrentSubtitle}
                            onChange={(event) => onSettingChanged('autoCopyCurrentSubtitle', event.target.checked)}
                        />
                    }
                    label={t('settings.autoCopy')}
                    labelPlacement="start"
                />
                {(!extensionInstalled || extensionSupportsAutoCopyableTrackSetting) && (
                    <FormControl>
                        <FormLabel component="legend">{t('settings.autoCopyableTracks')}</FormLabel>
                        <FormGroup>
                            {[0, 1, 2].map((trackIndex) => {
                                return (
                                    <FormControlLabel
                                        key={trackIndex}
                                        control={
                                            <Checkbox
                                                checked={isTrackAutoCopyable(autoCopyableTracks, trackIndex)}
                                                onChange={(event) => {
                                                    void onSettingChanged(
                                                        'autoCopyableTracks',
                                                        updateAutoCopyableTracksValue(
                                                            autoCopyableTracks,
                                                            trackIndex,
                                                            event.target.checked
                                                        )
                                                    );
                                                }}
                                            />
                                        }
                                        label={t('settings.subtitleTrackChoice', { trackNumber: trackIndex + 1 })}
                                    />
                                );
                            })}
                        </FormGroup>
                    </FormControl>
                )}
                {(!extensionInstalled || extensionSupportsSeekableTrackSetting) && (
                    <FormControl>
                        <FormLabel component="legend" sx={{ display: 'flex' }}>
                            {t('settings.seekableTracks')}
                            <KeyboardShortcutLink onClick={onViewSubtitleKeyboardShortcuts} preset="formLabel" />
                        </FormLabel>
                        <FormGroup>
                            {[0, 1, 2].map((trackIndex) => {
                                return (
                                    <FormControlLabel
                                        key={trackIndex}
                                        control={
                                            <Checkbox
                                                checked={isTrackSeekable(seekableTracks, trackIndex)}
                                                onChange={(event) => {
                                                    void onSettingChanged(
                                                        'seekableTracks',
                                                        updateSeekableTracksValue(
                                                            seekableTracks,
                                                            trackIndex,
                                                            event.target.checked
                                                        )
                                                    );
                                                }}
                                            />
                                        }
                                        label={t('settings.subtitleTrackChoice', { trackNumber: trackIndex + 1 })}
                                    />
                                );
                            })}
                        </FormGroup>
                    </FormControl>
                )}
                {insideApp && (
                    <>
                        <SwitchLabelWithHoverEffect
                            control={
                                <Switch
                                    checked={thumbnailPreview}
                                    onChange={() => onSettingChanged('thumbnailPreview', !thumbnailPreview)}
                                />
                            }
                            label={t('settings.thumbnailPreview')}
                            labelPlacement="start"
                        />
                        <SwitchLabelWithHoverEffect
                            control={
                                <Switch
                                    checked={subtitleAboveThumbnail}
                                    onChange={() => onSettingChanged('subtitleAboveThumbnail', !subtitleAboveThumbnail)}
                                    disabled={!thumbnailPreview}
                                />
                            }
                            label={t('settings.subtitleAboveThumbnail')}
                            labelPlacement="start"
                        />
                    </>
                )}
                <SettingsTextField
                    label={t('settings.subtitleRegexFilter')}
                    fullWidth
                    value={subtitleRegexFilter}
                    color="primary"
                    error={!validRegex}
                    helperText={!validRegex ? 'Invalid regular expression' : undefined}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title={t('settings.subtitleRegexFilterDocs')}>
                                        <IconButton
                                            component="a"
                                            href="https://docs.asbplayer.dev/docs/guides/subtitle-text-filtering"
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label={t('settings.subtitleRegexFilterDocs')}
                                        >
                                            <InfoIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        },
                    }}
                    onChange={(event) => onSettingChanged('subtitleRegexFilter', event.target.value)}
                />
                <SettingsTextField
                    label={t('settings.subtitleRegexFilterTextReplacement')}
                    fullWidth
                    value={subtitleRegexFilterTextReplacement}
                    color="primary"
                    onChange={(event) => onSettingChanged('subtitleRegexFilterTextReplacement', event.target.value)}
                />
                <FormControl>
                    <FormLabel>{t('settings.subtitleHtml')}</FormLabel>
                    <RadioGroup row>
                        <LabelWithHoverEffect
                            control={
                                <Radio
                                    checked={subtitleHtml === SubtitleHtml.remove}
                                    value={SubtitleHtml.remove}
                                    onChange={(event) =>
                                        event.target.checked &&
                                        void onSettingChanged('subtitleHtml', SubtitleHtml.remove)
                                    }
                                />
                            }
                            label={t('settings.subtitleHtmlRemove')}
                        />
                        <LabelWithHoverEffect
                            control={
                                <Radio
                                    checked={subtitleHtml === SubtitleHtml.render}
                                    value={SubtitleHtml.render}
                                    onChange={(event) =>
                                        event.target.checked &&
                                        void onSettingChanged('subtitleHtml', SubtitleHtml.render)
                                    }
                                />
                            }
                            label={t('settings.subtitleHtmlRender')}
                        />
                    </RadioGroup>
                </FormControl>
                <SwitchLabelWithHoverEffect
                    control={
                        <Switch
                            checked={convertNetflixRuby}
                            onChange={(event) => onSettingChanged('convertNetflixRuby', event.target.checked)}
                        />
                    }
                    label={t('settings.convertNetflixRuby')}
                    labelPlacement="start"
                />
                {(!extensionInstalled || extensionSupportsPauseOnHover) && (
                    <FormControl>
                        <FormLabel component="legend">{t('settings.pauseOnHoverMode')}</FormLabel>
                        <RadioGroup row={false}>
                            <LabelWithHoverEffect
                                control={
                                    <Radio
                                        checked={pauseOnHoverMode === PauseOnHoverMode.disabled}
                                        value={PauseOnHoverMode.disabled}
                                        onChange={(event) =>
                                            event.target.checked &&
                                            void onSettingChanged('pauseOnHoverMode', PauseOnHoverMode.disabled)
                                        }
                                    />
                                }
                                label={t('pauseOnHoverMode.disabled')}
                            />
                            <LabelWithHoverEffect
                                control={
                                    <Radio
                                        checked={pauseOnHoverMode === PauseOnHoverMode.inAndOut}
                                        value={PauseOnHoverMode.inAndOut}
                                        onChange={(event) =>
                                            event.target.checked &&
                                            void onSettingChanged('pauseOnHoverMode', PauseOnHoverMode.inAndOut)
                                        }
                                    />
                                }
                                label={t('pauseOnHoverMode.inAndOut')}
                            />
                            <LabelWithHoverEffect
                                control={
                                    <Radio
                                        checked={pauseOnHoverMode === PauseOnHoverMode.inNotOut}
                                        value={PauseOnHoverMode.inNotOut}
                                        onChange={(event) =>
                                            event.target.checked &&
                                            void onSettingChanged('pauseOnHoverMode', PauseOnHoverMode.inNotOut)
                                        }
                                    />
                                }
                                label={t('pauseOnHoverMode.inNotOut')}
                            />
                        </RadioGroup>
                    </FormControl>
                )}
                <SettingsSection>
                    {t('settings.playbackModes')}
                    <KeyboardShortcutLink onClick={onViewPlaybackModeKeyboardShortcuts} />
                </SettingsSection>
                {supportsPlaybackEngine && (
                    <>
                        <NumericSettingInput
                            fullWidth
                            label={
                                <>
                                    {t('settings.playbackRate')}
                                    <KeyboardShortcutLink
                                        onClick={onViewPlaybackRateKeyboardShortcuts}
                                        preset="numericalInputLabel"
                                    />
                                </>
                            }
                            value={playbackRate}
                            color="primary"
                            normalizeValue={normalizePlaybackRate}
                            onValueChange={(value) => void onSettingChanged('playbackRate', value)}
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    step: 0.05,
                                },
                            }}
                        />
                        <SwitchLabelWithHoverEffect
                            control={
                                <Switch
                                    checked={playbackRateNotificationEnabled}
                                    onChange={(event) =>
                                        onSettingChanged('playbackRateNotificationEnabled', event.target.checked)
                                    }
                                />
                            }
                            label={t('settings.playbackRateNotificationEnabled')}
                            labelPlacement="start"
                        />
                        <SwitchLabelWithHoverEffect
                            control={
                                <Switch
                                    checked={rememberPlaybackRate}
                                    onChange={(event) => onSettingChanged('rememberPlaybackRate', event.target.checked)}
                                />
                            }
                            label={t('settings.rememberPlaybackRate')}
                            labelPlacement="start"
                        />
                        <SwitchLabelWithHoverEffect
                            control={
                                <Switch
                                    checked={rememberPlaybackModes}
                                    onChange={(event) =>
                                        onSettingChanged('rememberPlaybackModes', event.target.checked)
                                    }
                                />
                            }
                            label={t('settings.rememberPlaybackModes')}
                            labelPlacement="start"
                        />
                    </>
                )}
                <SettingsSubSection>{t('settings.subtitleTriggers')}</SettingsSubSection>
                <FormControl>
                    <FormLabel component="legend">{t('settings.autoPausePreference')}</FormLabel>
                    {supportsPlaybackEngine ? (
                        <>
                            <FormGroup row>
                                <LabelWithHoverEffect
                                    control={
                                        <Checkbox
                                            checked={autoPauseAtStart}
                                            onChange={(event) =>
                                                handleAutoPausePreferenceChanged(
                                                    AutoPausePreference.atStart,
                                                    event.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label={t('settings.autoPauseAtSubtitleStart')}
                                />
                                <LabelWithHoverEffect
                                    control={
                                        <Checkbox
                                            checked={autoPauseAtEnd}
                                            onChange={(event) =>
                                                handleAutoPausePreferenceChanged(
                                                    AutoPausePreference.atEnd,
                                                    event.target.checked
                                                )
                                            }
                                        />
                                    }
                                    label={t('settings.autoPauseAtSubtitleEnd')}
                                />
                            </FormGroup>
                            <Typography variant="caption" color="textSecondary">
                                {t('settings.autoPausePreferenceHelperText')}
                            </Typography>
                        </>
                    ) : (
                        <RadioGroup row>
                            <LabelWithHoverEffect
                                control={
                                    <Radio
                                        checked={autoPausePreference === AutoPausePreference.atStart}
                                        value={AutoPausePreference.atStart}
                                        onChange={(event) =>
                                            event.target.checked &&
                                            void onSettingChanged('autoPausePreference', AutoPausePreference.atStart)
                                        }
                                    />
                                }
                                label={t('settings.autoPauseAtSubtitleStart')}
                            />
                            <LabelWithHoverEffect
                                control={
                                    <Radio
                                        checked={autoPausePreference === AutoPausePreference.atEnd}
                                        value={AutoPausePreference.atEnd}
                                        onChange={(event) =>
                                            event.target.checked &&
                                            void onSettingChanged('autoPausePreference', AutoPausePreference.atEnd)
                                        }
                                    />
                                }
                                label={t('settings.autoPauseAtSubtitleEnd')}
                            />
                        </RadioGroup>
                    )}
                </FormControl>
                {supportsAutoPauseResume && (
                    <>
                        <FormControl>
                            <FormLabel component="legend" sx={{ display: 'flex' }}>
                                {t('settings.autoPauseResumeMode')}
                                <KeyboardShortcutLink
                                    onClick={onViewPlaybackModeKeyboardShortcuts}
                                    preset="formLabel"
                                />
                            </FormLabel>
                            <RadioGroup>
                                <LabelWithHoverEffect
                                    control={
                                        <Radio
                                            checked={autoPauseResumeMode === AutoPauseResumeMode.manual}
                                            value={AutoPauseResumeMode.manual}
                                            onChange={(event) =>
                                                event.target.checked &&
                                                void onSettingChanged('autoPauseResumeMode', AutoPauseResumeMode.manual)
                                            }
                                        />
                                    }
                                    label={t('settings.autoPauseResumeModeManual')}
                                />
                                <LabelWithHoverEffect
                                    control={
                                        <Radio
                                            checked={autoPauseResumeMode === AutoPauseResumeMode.fixed}
                                            value={AutoPauseResumeMode.fixed}
                                            onChange={(event) =>
                                                event.target.checked &&
                                                void onSettingChanged('autoPauseResumeMode', AutoPauseResumeMode.fixed)
                                            }
                                        />
                                    }
                                    label={t('settings.autoPauseResumeModeFixed')}
                                />
                                <LabelWithHoverEffect
                                    control={
                                        <Radio
                                            checked={autoPauseResumeMode === AutoPauseResumeMode.subtitleLength}
                                            value={AutoPauseResumeMode.subtitleLength}
                                            onChange={(event) =>
                                                event.target.checked &&
                                                void onSettingChanged(
                                                    'autoPauseResumeMode',
                                                    AutoPauseResumeMode.subtitleLength
                                                )
                                            }
                                        />
                                    }
                                    label={t('settings.autoPauseResumeModeSubtitleLength')}
                                />
                            </RadioGroup>
                        </FormControl>
                        {autoPauseResumeMode === AutoPauseResumeMode.fixed && (
                            <ResponsiveSettingsStack>
                                <NumericSettingInput
                                    color="primary"
                                    fullWidth
                                    label={t('settings.autoPauseFixedDuration')}
                                    value={autoPauseFixedDurationMs}
                                    onValueChange={(value) => void onSettingChanged('autoPauseFixedDurationMs', value)}
                                    slotProps={{
                                        htmlInput: { min: 0, step: 1 },
                                        input: { endAdornment: <InputAdornment position="end">ms</InputAdornment> },
                                    }}
                                />
                                <NumericSettingInput
                                    color="primary"
                                    fullWidth
                                    label={t('settings.autoPauseResumeDelay')}
                                    value={autoPauseResumeDelayMs}
                                    onValueChange={(value) => void onSettingChanged('autoPauseResumeDelayMs', value)}
                                    slotProps={{
                                        htmlInput: { min: 0, step: 1 },
                                        input: { endAdornment: <InputAdornment position="end">ms</InputAdornment> },
                                    }}
                                />
                            </ResponsiveSettingsStack>
                        )}
                        {autoPauseResumeMode === AutoPauseResumeMode.subtitleLength && (
                            <>
                                <ResponsiveSettingsStack>
                                    <NumericSettingInput
                                        color="primary"
                                        fullWidth
                                        label={t('settings.autoPauseMinimumDuration')}
                                        value={autoPauseMinimumDurationMs}
                                        onValueChange={handleAutoPauseMinimumDurationChanged}
                                        slotProps={{
                                            htmlInput: { min: 0, step: 1 },
                                            input: { endAdornment: <InputAdornment position="end">ms</InputAdornment> },
                                        }}
                                    />
                                    <NumericSettingInput
                                        color="primary"
                                        fullWidth
                                        label={t('settings.autoPauseMaximumDuration')}
                                        helperText={t('settings.autoPauseMaximumDurationHelperText')}
                                        value={autoPauseMaximumDurationMs}
                                        onValueChange={handleAutoPauseMaximumDurationChanged}
                                        slotProps={{
                                            htmlInput: { min: 0, step: 1 },
                                            input: { endAdornment: <InputAdornment position="end">ms</InputAdornment> },
                                        }}
                                    />
                                </ResponsiveSettingsStack>
                                <ResponsiveSettingsStack>
                                    <NumericSettingInput
                                        color="primary"
                                        fullWidth
                                        label={t('settings.autoPauseTimePerCharacter')}
                                        value={autoPauseTimePerCharacterMs}
                                        onValueChange={(value) =>
                                            void onSettingChanged('autoPauseTimePerCharacterMs', value)
                                        }
                                        slotProps={{
                                            htmlInput: { min: 0, step: 1 },
                                            input: { endAdornment: <InputAdornment position="end">ms</InputAdornment> },
                                        }}
                                    />
                                    <NumericSettingInput
                                        color="primary"
                                        fullWidth
                                        label={t('settings.autoPauseResumeDelay')}
                                        value={autoPauseResumeDelayMs}
                                        onValueChange={(value) =>
                                            void onSettingChanged('autoPauseResumeDelayMs', value)
                                        }
                                        slotProps={{
                                            htmlInput: { min: 0, step: 1 },
                                            input: { endAdornment: <InputAdornment position="end">ms</InputAdornment> },
                                        }}
                                    />
                                </ResponsiveSettingsStack>
                            </>
                        )}
                        <FormControl>
                            <FormLabel component="legend" sx={{ display: 'flex' }}>
                                {t('settings.subtitleVisibility')}
                                <KeyboardShortcutLink
                                    onClick={onViewPlaybackModeKeyboardShortcuts}
                                    preset="formLabel"
                                />
                            </FormLabel>
                            <RadioGroup row>
                                <LabelWithHoverEffect
                                    control={
                                        <Radio
                                            checked={subtitleVisibility === SubtitleVisibility.whenDue}
                                            value={SubtitleVisibility.whenDue}
                                            onChange={(event) =>
                                                event.target.checked &&
                                                void onSettingChanged('subtitleVisibility', SubtitleVisibility.whenDue)
                                            }
                                        />
                                    }
                                    label={t('settings.subtitleVisibilityWhenDue')}
                                />
                                <LabelWithHoverEffect
                                    control={
                                        <Radio
                                            checked={subtitleVisibility === SubtitleVisibility.whilePaused}
                                            value={SubtitleVisibility.whilePaused}
                                            onChange={(event) =>
                                                event.target.checked &&
                                                void onSettingChanged(
                                                    'subtitleVisibility',
                                                    SubtitleVisibility.whilePaused
                                                )
                                            }
                                        />
                                    }
                                    label={t('settings.subtitleVisibilityWhilePaused')}
                                />
                            </RadioGroup>
                        </FormControl>
                    </>
                )}
                {supportsPlaybackEngine && (
                    <>
                        <NumericSettingInput
                            color="primary"
                            fullWidth
                            label={t('settings.repeatCountPreference')}
                            helperText={t('settings.repeatCountPreferenceHelperText')}
                            value={repeatCountPreference}
                            onValueChange={(value) => void onSettingChanged('repeatCountPreference', value)}
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    step: 1,
                                },
                            }}
                        />
                        <ResponsiveSettingsStack>
                            <NumericSettingInput
                                color="primary"
                                fullWidth
                                label={t('settings.subtitleTriggerStartOffset')}
                                value={subtitleTriggerStartOffset}
                                onValueChange={(value) => void onSettingChanged('subtitleTriggerStartOffset', value)}
                                slotProps={{
                                    htmlInput: {
                                        step: 1,
                                    },
                                    input: {
                                        endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                                    },
                                }}
                            />
                            <NumericSettingInput
                                color="primary"
                                fullWidth
                                label={t('settings.subtitleTriggerEndOffset')}
                                value={subtitleTriggerEndOffset}
                                onValueChange={(value) => void onSettingChanged('subtitleTriggerEndOffset', value)}
                                slotProps={{
                                    htmlInput: {
                                        step: 1,
                                    },
                                    input: {
                                        endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                                    },
                                }}
                            />
                        </ResponsiveSettingsStack>
                        <Typography variant="caption" color="textSecondary">
                            {t('settings.subtitleTriggerOffsetHelperText')}
                        </Typography>
                    </>
                )}
                <SettingsSubSection>{t('settings.subtitleGapTriggers')}</SettingsSubSection>
                <ResponsiveSettingsStack>
                    {supportsPlaybackEngine && (
                        <NumericSettingInput
                            color="primary"
                            fullWidth
                            label={t('settings.fastForwardPlaybackMinimumSkipInterval')}
                            value={fastForwardPlaybackMinimumSkipIntervalMs}
                            onValueChange={(value) =>
                                void onSettingChanged('fastForwardPlaybackMinimumSkipIntervalMs', value)
                            }
                            slotProps={{
                                htmlInput: {
                                    min: 0,
                                    step: 1,
                                },
                                input: {
                                    endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                                },
                            }}
                        />
                    )}
                    <NumericSettingInput
                        fullWidth
                        label={t('settings.fastForwardModePlaybackRate')}
                        value={fastForwardModePlaybackRate}
                        color="primary"
                        normalizeValue={normalizePlaybackRate}
                        onValueChange={(value) => void onSettingChanged('fastForwardModePlaybackRate', value)}
                        slotProps={{
                            htmlInput: {
                                min: 0,
                                step: 0.05,
                            },
                        }}
                    />
                </ResponsiveSettingsStack>
                <NumericSettingInput
                    color="primary"
                    fullWidth
                    label={t('settings.condensedPlaybackMinimumSkipInterval')}
                    value={streamingCondensedPlaybackMinimumSkipIntervalMs}
                    onValueChange={(value) =>
                        void onSettingChanged('streamingCondensedPlaybackMinimumSkipIntervalMs', value)
                    }
                    slotProps={{
                        htmlInput: {
                            min: 0,
                            step: 1,
                        },
                        input: {
                            endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                        },
                    }}
                />
                {supportsPlaybackEngine && (
                    <>
                        <ResponsiveSettingsStack>
                            <NumericSettingInput
                                color="primary"
                                fullWidth
                                label={t('settings.subtitleTriggerGapStartOffset')}
                                value={subtitleTriggerGapStartOffset}
                                onValueChange={(value) => void onSettingChanged('subtitleTriggerGapStartOffset', value)}
                                slotProps={{
                                    htmlInput: {
                                        min: 0,
                                        step: 1,
                                    },
                                    input: {
                                        endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                                    },
                                }}
                            />
                            <NumericSettingInput
                                color="primary"
                                fullWidth
                                label={t('settings.subtitleTriggerGapEndOffset')}
                                value={subtitleTriggerGapEndOffset}
                                onValueChange={(value) => void onSettingChanged('subtitleTriggerGapEndOffset', value)}
                                slotProps={{
                                    htmlInput: {
                                        max: 0,
                                        step: 1,
                                    },
                                    input: {
                                        endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                                    },
                                }}
                            />
                        </ResponsiveSettingsStack>
                        <Typography variant="caption" color="textSecondary">
                            {t('settings.subtitleTriggerGapOffsetHelperText')}
                        </Typography>
                    </>
                )}
                <SettingsSection>{t('settings.webSocketInterface')}</SettingsSection>
                <SwitchLabelWithHoverEffect
                    control={
                        <Switch
                            checked={webSocketClientEnabled}
                            onChange={(e) => onSettingChanged('webSocketClientEnabled', e.target.checked)}
                        />
                    }
                    label={t('settings.webSocketClientEnabled')}
                    labelPlacement="start"
                />
                <SettingsTextField
                    color="primary"
                    fullWidth
                    label={t('settings.webSocketServerUrl')}
                    value={webSocketServerUrl}
                    disabled={!webSocketClientEnabled}
                    onChange={(e) => onSettingChanged('webSocketServerUrl', e.target.value)}
                    error={webSocketClientEnabled && webSocketConnectionSucceeded === false}
                    helperText={webSocketServerUrlHelperText}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={pingWebSocketServer}>
                                        <RefreshIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <SettingsSection>{t('settings.mining')}</SettingsSection>
                <NumericSettingInput
                    label={t('settings.miningHistoryStorageLimit')}
                    fullWidth
                    value={miningHistoryStorageLimit}
                    color="primary"
                    onValueChange={(value) => void onSettingChanged('miningHistoryStorageLimit', value)}
                    slotProps={{
                        htmlInput: {
                            min: 0,
                            step: 1,
                        },
                    }}
                />
                {insideApp && (
                    <SettingsTextField
                        label={t('settings.tabName')}
                        fullWidth
                        value={tabName}
                        color="primary"
                        onChange={(event) => onSettingChanged('tabName', event.target.value)}
                    />
                )}
            </Stack>
            <input
                ref={settingsFileInputRef}
                onChange={handleSettingsFileInputChange}
                type="file"
                accept=".json"
                multiple
                hidden
            />
            <LogViewerDialog open={logViewerOpen} onClose={() => setLogViewerOpen(false)} logProvider={logProvider} />
        </>
    );
};

export default MiscSettingTab;
